import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, MessageCircle, Mail, Loader2 } from "lucide-react";

export default function ShareReportDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const loadReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/report");
      setText(data.text);
      setReportDate(data.date);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  // Load report each time dialog opens (from parent or internally)
  useEffect(() => {
    if (open) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (v) => {
    onOpenChange(v);
  };

  const sendWhatsApp = () => {
    const num = phone.replace(/[^\d]/g, "");
    if (!num) return toast.error("Enter a phone number (with country code)");
    const url = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp…");
  };

  const sendEmail = () => {
    if (!email.trim()) return toast.error("Enter an email address");
    const subject = `Carmel SSS — Inspection Report (${reportDate})`;
    const url = `mailto:${encodeURIComponent(email.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    window.location.href = url;
    toast.success("Opening email app…");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Share Daily Report</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-carmel-gold" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Report Preview (editable)</Label>
              <Textarea
                rows={12}
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="mt-1 font-mono text-xs"
                data-testid="share-report-text"
              />
              <p className="text-[11px] text-carmel-navy/50 mt-1">Tip: edit the message before sending if you want to add a personal note.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-carmel-navy/10">
              <div>
                <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-carmel-navy/60">
                  <MessageCircle className="w-3.5 h-3.5" /> Send via WhatsApp
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="+919876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="share-whatsapp-input"
                  />
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={sendWhatsApp} data-testid="share-whatsapp-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-carmel-navy/50 mt-1">Include country code, e.g. +91…</p>
              </div>
              <div>
                <Label className="flex items-center gap-2 text-xs uppercase tracking-wider text-carmel-navy/60">
                  <Mail className="w-3.5 h-3.5" /> Send via Email
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="principal@carmel.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="share-email-input"
                  />
                  <Button className="bg-carmel-navy hover:bg-carmel-navyMid" onClick={sendEmail} data-testid="share-email-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-carmel-navy/50 mt-1">Opens your default email app.</p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="share-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
